import type { ChartConfig, ChartData } from '@indutrust/chart-model';
import {
  createAreaChartOptions,
  createBarChartOptions,
  createHorizontalBarChartOptions,
  createLineChartOptions,
  createStackedBarChartOptions,
} from './cartesian-option';
import type { ChartOptionBuildOptions } from './chart-option-chrome';
import { createDonutChartOptions, createPieChartOptions } from './pie-option';
import { createSparklineChartOptions } from './sparkline-option';
import type { SupportedChartConfiguration } from './supported-chart-configuration';

/**
 * Generate an ECharts configuration based on the {@link ChartConfig.type}.
 *
 * Delegates to the matching `create*ChartOptions` helper. Radar, scatter,
 * gauge, and heatmap are **not** dispatched here — call their dedicated
 * builders instead.
 *
 * @param data The input series and labels. {@link ChartData}
 * @param config Chart configuration containing the `type`. {@link ChartConfig}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A {@link SupportedChartConfiguration} matching the requested chart type.
 * @example
 * const opt = generateChartOptions(data, { type: 'bar', xAxisLabel: 'Q', yAxisLabel: 'Revenue' });
 */
export function generateChartOptions(
  data: ChartData,
  config: ChartConfig,
  buildOptions: ChartOptionBuildOptions = {}
): SupportedChartConfiguration {
  switch (config.type) {
    case 'line':
      return createLineChartOptions(data, config, buildOptions);
    case 'bar':
      return createBarChartOptions(data, config, buildOptions);
    case 'pie':
      return createPieChartOptions(data, config, buildOptions);
    case 'area':
      return createAreaChartOptions(data, config, buildOptions);
    case 'donut':
      return createDonutChartOptions(data, config, buildOptions);
    case 'stacked-bar':
      return createStackedBarChartOptions(data, config, buildOptions);
    case 'horizontal-bar':
      return createHorizontalBarChartOptions(data, config, buildOptions);
    case 'sparkline':
      return createSparklineChartOptions(data, config, buildOptions);
  }
}
